import React, { useRef, useState } from 'react';
import ColorPickerPopover from './ColorPickerPopover';
import { Icon } from './Icon';

const HEX_RE = /^#[0-9A-F]{6}$/;
const DEFAULT_MAX = 6;
const DEFAULT_NEW = '#888888';

export interface ColorHexInputProps {
  /** Comma-separated `#RRGGBB` list. First = primary, rest secondary. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
  /** aria/title for the "+" add affordance. */
  addLabel?: string;
  /** aria for a chip's remove button. */
  removeLabel?: string;
  /** Label for the colour-picker's Apply button (i18n). */
  applyLabel?: string;
  /** Max colours (default 6 — matches `useParsedColors`). */
  max?: number;
  /** Short warning shown to the left of hint (e.g. "colours are approximate"). */
  warning?: string;
}

/**
 * Chip-based colour editor for the master-catalog material forms (filament /
 * resin). Each colour is a circle chip; clicking a chip (or the dashed "+"
 * circle) opens the BolloPrint-styled `ColorPickerPopover`, where the choice is
 * staged and committed once (Enter / Apply / click-outside) — so picking never
 * spams a new colour per click. Typing the hex code lives inside that picker.
 * Stores back the SAME comma-separated `#RRGGBB` string the forms persist
 * (see `catalogForm.useParsedColors`), so nothing downstream changes.
 * Shared by tenant + admin catálogo.
 */
export default function ColorHexInput({
  value,
  onChange,
  label,
  hint,
  disabled = false,
  addLabel = 'Añadir color',
  removeLabel = 'Quitar color',
  applyLabel,
  max = DEFAULT_MAX,
  warning,
}: ColorHexInputProps) {
  const colors = value.split(',').map((s) => s.trim()).filter(Boolean);
  const full = colors.length >= max;
  const triggerRef = useRef<HTMLElement | null>(null);
  const [picker, setPicker] = useState<{ mode: 'add' | 'edit'; index: number } | null>(null);

  const commit = (next: string[]) => onChange(next.slice(0, max).join(', '));
  const updateAt = (i: number, hex: string) => commit(colors.map((c, idx) => (idx === i ? hex : c)));
  const removeAt = (i: number) => commit(colors.filter((_, idx) => idx !== i));
  const add = (hex: string) => {
    if (!full) commit([...colors, hex]);
  };

  const openPicker = (e: React.MouseEvent, next: { mode: 'add' | 'edit'; index: number }) => {
    triggerRef.current = e.currentTarget as HTMLElement;
    setPicker(next);
  };

  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
          {label}
        </label>
      )}
      <div className="flex flex-wrap items-center gap-2 min-h-[2.75rem] px-2 py-2 rounded-md border border-border-strong dark:border-border-strong-dark bg-white dark:bg-surface-dark">
        {colors.map((hex, i) => (
          <span
            key={`${hex}-${i}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 dark:bg-slate-800 pl-1 pr-2 py-0.5"
          >
            <button
              type="button"
              disabled={disabled}
              onClick={(e) => openPicker(e, { mode: 'edit', index: i })}
              title={hex}
              aria-label={hex}
              className="h-6 w-6 shrink-0 rounded-full border border-border-strong dark:border-border-strong-dark shadow-sm transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{ backgroundColor: HEX_RE.test(hex) ? hex : '#000000' }}
            />
            <span className="font-mono text-xs text-slate-700 dark:text-slate-200">{hex}</span>
            {!disabled && (
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="text-slate-400 transition-colors hover:text-red-500"
                aria-label={removeLabel}
              >
                <Icon name="close" className="!text-[14px]" />
              </button>
            )}
          </span>
        ))}

        {!disabled && !full && (
          <button
            type="button"
            onClick={(e) => openPicker(e, { mode: 'add', index: -1 })}
            title={addLabel}
            aria-label={addLabel}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-border-strong text-slate-400 transition-colors hover:border-primary hover:text-primary dark:border-border-strong-dark"
          >
            <Icon name="add" className="!text-[18px]" />
          </button>
        )}
      </div>
      {(warning || hint) && (
        <p className="mt-1 flex gap-2 text-[11px]">
          {warning && <span className="font-medium text-amber-500 dark:text-amber-400">{warning}</span>}
          {hint && <span className="text-slate-400 dark:text-slate-500">{hint}</span>}
        </p>
      )}

      {picker && (
        <ColorPickerPopover
          anchorRef={triggerRef}
          initialColor={picker.mode === 'edit' ? colors[picker.index] || DEFAULT_NEW : DEFAULT_NEW}
          onCommit={(hex) => (picker.mode === 'edit' ? updateAt(picker.index, hex) : add(hex))}
          onClose={() => setPicker(null)}
          applyLabel={applyLabel}
        />
      )}
    </div>
  );
}
